import { isEmpty } from 'lodash/fp';

import { DataKey, DataRecord, DataResponse, PaginatedResponse } from '@portal/common/types';
import addColumnToDataResponse from '@portal/common/utility/data/add-column-to-data-response';

import {
    AnalyticsAPIParams,
    DELIVERY_METHOD_KEY,
    DeliveryMethod,
} from '../../store/insights/models';

const ENDPOINT_BY_DELIVERY_METHOD = {
    [DeliveryMethod.download]: 'stats/pdf-exports',
    [DeliveryMethod.email]: 'stats/pdf-reports',
};

type PagedResponse = DataResponse & Partial<PaginatedResponse<DataRecord>>;

type Fetcher = (resource: string, params: AnalyticsAPIParams) => Promise<PagedResponse>;

const getRecords = (response: PagedResponse): DataRecord[] =>
    response.records || response.results || [];

export const requestPdfExportsByDeliveryMethod = async (
    params: AnalyticsAPIParams,
    fetcher: Fetcher
): Promise<PagedResponse> => {
    const { delivery_method: deliveryMethod, ...rest } = params;

    const requestedColumns = (rest.group_by || '').split(',').filter(Boolean);
    const isDeliveryMethodColumn = requestedColumns.includes(DELIVERY_METHOD_KEY);
    const supportedColumns = requestedColumns.filter((column) => column !== DELIVERY_METHOD_KEY);

    const apiParams: AnalyticsAPIParams = {
        ...rest,
        group_by: isEmpty(supportedColumns) ? undefined : supportedColumns.join(','),
    };

    if (apiParams.order_by === DELIVERY_METHOD_KEY) {
        delete apiParams.order_by;
        delete apiParams.order_direction;
    }

    const method = (deliveryMethod as DeliveryMethod) || DeliveryMethod.download;
    const response = await fetcher(ENDPOINT_BY_DELIVERY_METHOD[method], apiParams);

    if (!isDeliveryMethodColumn) {
        return response;
    }

    const { columns, records } = addColumnToDataResponse(
        { columns: response.columns || [], records: getRecords(response) },
        DELIVERY_METHOD_KEY as DataKey,
        () => method
    );

    return response.paging
        ? { columns, results: records, paging: response.paging }
        : { columns, records };
};
